import React, { useState, useEffect } from 'react';
import { tasksApi } from '../../api/tasks';
import { documentsApi } from '../../api/documents';
import { recordsApi } from '../../api/records';
import { AlertTriangle, CheckCircle2, FileText, Clock, DollarSign, Calendar, Activity } from 'lucide-react';
import WidgetCard from './WidgetCard';
import WidgetModal from './WidgetModal';

function Stat({ icon, label, value, sub, color }) {
  return (
    <div className="flex items-start gap-2.5 p-2.5 bg-gray-50 rounded-lg">
      <div className={`p-1.5 rounded-lg ${color || 'bg-blue-100 text-blue-600'}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-gray-900">{value}</p>
        {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
      </div>
    </div>
  );
}

export default function VitalsWidget({ recordId, record, fields, fieldData }) {
  const [taskSummary, setTaskSummary] = useState(null);
  const [docCount, setDocCount] = useState(0);
  const [lastActivity, setLastActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      tasksApi.getForRecord(recordId).then(r => r.data || r).catch(() => ({ grouped: {} })),
      documentsApi.getForRecord(recordId).catch(() => ({ documents: [] })),
      recordsApi.getHistory(recordId).then(r => r.data).catch(() => ({ history: [] })),
    ]).then(([taskData, docData, histData]) => {
      if (!mounted) return;
      const allTasks = [];
      Object.values(taskData.grouped || {}).forEach(arr => allTasks.push(...arr));
      const now = new Date();
      const open = allTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
      const overdue = open.filter(t => t.due_date && new Date(t.due_date) < now);
      const completed = allTasks.filter(t => t.status === 'completed');
      setTaskSummary({ total: allTasks.length, open: open.length, overdue: overdue.length, completed: completed.length });
      setDocCount((docData.documents || []).length);
      const hist = histData.history || [];
      setLastActivity(hist.length > 0 ? hist[0] : null);
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [recordId]);

  // Case age
  const caseAge = record ? Math.floor((Date.now() - new Date(record.created_at).getTime()) / 86400000) : 0;

  // Find currency fields for medical bills & PD costs
  const currencyFields = fields.filter(f => f.field_type === 'CURRENCY');
  const medField = currencyFields.find(f => /medical|med.*bill/i.test(f.label));
  const pdField = currencyFields.find(f => /property.*damage|pd.*cost|pd/i.test(f.label));
  const medVal = medField ? fieldData[medField.id] : null;
  const pdVal = pdField ? fieldData[pdField.id] : null;

  // Statute of limitations — look for date field
  const solField = fields.find(f => f.field_type === 'DATE' && /statute|limitation|sol|deadline/i.test(f.label));
  const solDate = solField ? fieldData[solField.id] : null;
  const solDays = solDate ? Math.ceil((new Date(solDate).getTime() - Date.now()) / 86400000) : null;

  const formatMoney = (v) => v ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 0 }) : '—';

  const lastActText = lastActivity
    ? `${lastActivity.first_name} — ${new Date(lastActivity.created_at).toLocaleDateString('en-US', { month:'short', day:'numeric' })}`
    : 'None';

  const stats = [
    { icon: <Calendar className="h-3.5 w-3.5" />, label: 'Case Age', value: caseAge + ' days', color: 'bg-blue-100 text-blue-600' },
    ...(solDays !== null ? [{ icon: <AlertTriangle className="h-3.5 w-3.5" />, label: 'Statute (SOL)', value: solDays + ' days', sub: solDays <= 30 ? '⚠ Approaching!' : '', color: solDays <= 30 ? 'bg-red-100 text-red-600' : solDays <= 90 ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600' }] : []),
    { icon: <CheckCircle2 className="h-3.5 w-3.5" />, label: 'Tasks', value: taskSummary ? `${taskSummary.open} open` : '—', sub: taskSummary?.overdue ? `${taskSummary.overdue} overdue` : '', color: taskSummary?.overdue ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600' },
    { icon: <FileText className="h-3.5 w-3.5" />, label: 'Documents', value: String(docCount), color: 'bg-purple-100 text-purple-600' },
    ...(medField ? [{ icon: <DollarSign className="h-3.5 w-3.5" />, label: medField.label, value: formatMoney(medVal), color: 'bg-emerald-100 text-emerald-600' }] : []),
    ...(pdField ? [{ icon: <DollarSign className="h-3.5 w-3.5" />, label: pdField.label, value: formatMoney(pdVal), color: 'bg-amber-100 text-amber-600' }] : []),
    { icon: <Activity className="h-3.5 w-3.5" />, label: 'Last Activity', value: lastActText, color: 'bg-gray-100 text-gray-600' },
  ];

  if (loading) return <WidgetCard title="Vitals" icon="💊"><p className="text-xs text-gray-400 p-4">Loading...</p></WidgetCard>;

  return (
    <>
      <WidgetCard title="Vitals" icon="💊" onExpand={() => setModalOpen(true)}>
        <div className="p-3 grid grid-cols-2 gap-2">
          {stats.slice(0, 4).map((s, i) => <Stat key={i} {...s} />)}
        </div>
      </WidgetCard>
      <WidgetModal open={modalOpen} onClose={() => setModalOpen(false)} title="Case Vitals" icon="💊">
        <div className="p-6 grid grid-cols-2 gap-3">
          {stats.map((s, i) => <Stat key={i} {...s} />)}
        </div>
        {currencyFields.length > 0 && (
          <div className="px-6 pb-6">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">All Financial Fields</p>
            <div className="space-y-2">
              {currencyFields.map(f => (
                <div key={f.id} className="flex justify-between text-sm p-2 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">{f.label}</span>
                  <span className="font-medium text-gray-900">{formatMoney(fieldData[f.id])}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </WidgetModal>
    </>
  );
}
